import React, { useState, useEffect } from "react";
import { Grid, Button, Typography, Box, IconButton, TextField, CircularProgress } from "@mui/material";
import LandingImg2 from "../../Assets/BusBookingImg/LandingImg3.jpg";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";

const BusBookingForm1 = () => {
  const [fromStation, setFromStation] = useState(null);
  const [toStation, setToStation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [originCities, setOriginCities] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  // Debounced fetch functions for origin and destination
  const debouncedFetchOrigin = debounce((searchTerm) => {
    fetchCities(searchTerm, 'origin');
  }, 300);

  const debouncedFetchDestination = debounce((searchTerm) => {
    fetchCities(searchTerm, 'destination');
  }, 300);

  const fetchCities = async (searchTerm = '', type = 'origin') => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    try {
      setLoading(true);
      const url = new URL('https://api.ipaisa.co.in/myapp/bus/citylist/search');
      url.searchParams.append('searchTerm', searchTerm);
      url.searchParams.append('type', type);

      const response = await axios.get(
        url.toString(),
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );

      if (response.data?.BusCities) {
        if (type === 'origin') {
          setOriginCities(response.data.BusCities);
        } else {
          setDestinationCities(response.data.BusCities);
        }
      } else {
        console.error("Invalid city list format:", response.data);
        if (type === 'origin') {
          setOriginCities([]);
        } else {
          setDestinationCities([]);
        }
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      if (type === 'origin') {
        setOriginCities([]);
      } else {
        setDestinationCities([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load with empty search to get default cities
    fetchCities('', 'origin');
    fetchCities('', 'destination');
  }, []);

  const handleSwapStations = () => {
    const temp = fromStation;
    setFromStation(toStation);
    setToStation(temp);
  };

  const searchBuses = async () => {
    if (!fromStation || !toStation || !selectedDate) {
      toast.error("Please select From City, To City, and Date of Journey.");
      return;
    }

    const requestBody = {
      dateOfJourney: selectedDate,
      originId: fromStation.CityId,
      destinationId: toStation.CityId,
    
    };

    const params = {
      page: 1,
      size: 10
    };

    try {
      setSearching(true);
      const jwtToken = sessionStorage.getItem("jwtToken");
      const response = await axios.post(
        "https://api.ipaisa.co.in/myapp/bus/search",
        requestBody,
        { headers: { Authorization: `Bearer ${jwtToken}` },
           params:params }
      );

      if (response.data.data?.BusResults) {
        navigate("/bus-results", {
          state: {
            buses: response.data.data.BusResults.content,
            Origin: response.data.data.Origin,
            Destination: response.data.data.Destination,
            TraceId: response.data.data.TraceId,
            fromStation,
            toStation,
            selectedDate,
            sessionId:response.data.sessionId,
          currentPage: response.data.data.BusResults.currentPage,
          totalPages: response.data.data.BusResults.totalPages,
          totalItems: response.data.data.BusResults.totalElements
          },
        });
      } else {
        const errorMessage = response.data.BusSearchResult?.Error?.ErrorMessage || "No buses available for the selected route.";
        toast.warning(errorMessage);
      }
    } catch (error) {
      console.error("Error searching for buses:", error);
      toast.error("Failed to search buses.");
    } finally {
      setSearching(false);
    }
  };

  // Handle input change for origin autocomplete
  const handleOriginInputChange = (event, newValue) => {
    if (newValue) {
      debouncedFetchOrigin(newValue);
    }
  };

  // Handle input change for destination autocomplete
  const handleDestinationInputChange = (event, newValue) => {
    if (newValue) {
      debouncedFetchDestination(newValue);
    }
  };

  return (
    <Box>
      <ToastContainer />
      <Grid
        sx={{
          backgroundImage: `url(${LandingImg2})`,
          backgroundSize: "100% auto",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          textAlign: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: "bold", marginBottom: "20px", color:"#027BFE" }}>
          Book Bus Tickets
        </Typography>
        <Grid
          container
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: "10px",
            padding: "20px",
            maxWidth: "600px",
            color: "black",
          }}
        >
          {/* From Station */}
          <Grid item xs={12} sm={5} sx={{ padding: "10px" }}>
            <Autocomplete
              options={originCities}
              getOptionLabel={(option) => option.CityName}
              value={fromStation}
              onInputChange={handleOriginInputChange}
              onChange={(event, newValue) => setFromStation(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <DirectionsBusIcon sx={{ marginRight: "8px" }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Flip Icon */}
          <Grid
            item
            xs={12}
            sm={2}
            sx={{
              padding: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={handleSwapStations}
              sx={{ backgroundColor: "#007bff", color: "white" }}
            >
              <SwapHorizIcon />
            </IconButton>
          </Grid>

          {/* To Station */}
          <Grid item xs={12} sm={5} sx={{ padding: "10px" }}>
            <Autocomplete
              options={destinationCities}
              getOptionLabel={(option) => option.CityName}
              value={toStation}
              onInputChange={handleDestinationInputChange}
              onChange={(event, newValue) => setToStation(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <LocationOnIcon sx={{ marginRight: "8px" }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Date Picker */}
          <Box item xs={12} sm={6} sx={{ padding: "10px" }}>
            <TextField
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: {
                  min: new Date().toISOString().split('T')[0] // Set min date to today
                }
              }}
            />
          </Box>

          {/* Today and Tomorrow Buttons */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ padding: "10px", display: "flex", gap: "10px" }}
          >
            <Button
              fullWidth
              variant="outlined"
              sx={{ backgroundColor: "#027BFE", color: "white" }}
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
            >
              Today
            </Button>
            <Button
              fullWidth
              variant="outlined"
              sx={{ backgroundColor: "#027BFE", color: "white" }}
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setSelectedDate(tomorrow.toISOString().split('T')[0]);
              }}
            >
              Tomorrow
            </Button>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sx={{ padding: "10px" }}>
            <Button
              fullWidth
              variant="contained"
              sx={{ height: "56px", backgroundColor: "#027BFE" }}
              onClick={searchBuses}
              disabled={loading || searching}
            >
              {searching ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Search Bus"
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusBookingForm1;